<template>
  <form class="cm-form" @submit.prevent>
    <div class="cm-row" data-cols="2">
      <div class="cm-field" :class="{ 'cm-invalid': !!errors.firstName }">
        <label class="cm-label" for="cm-firstName">{{ t('contactForm.firstName') }}</label>
        <input
          class="cm-input"
          id="cm-firstName"
          name="firstName"
          type="text"
          autocomplete="given-name"
          :placeholder="t('contactForm.firstNamePlaceholder')"
          :value="model.firstName"
          @input="onInput('firstName', ($event.target as HTMLInputElement).value)"
        />
        <div class="cm-error">{{ errors.firstName }}</div>
      </div>
      <div class="cm-field" :class="{ 'cm-invalid': !!errors.lastName }">
        <label class="cm-label" for="cm-lastName">{{ t('contactForm.lastName') }}</label>
        <input
          class="cm-input"
          id="cm-lastName"
          name="lastName"
          type="text"
          autocomplete="family-name"
          :placeholder="t('contactForm.lastNamePlaceholder')"
          :value="model.lastName"
          @input="onInput('lastName', ($event.target as HTMLInputElement).value)"
        />
        <div class="cm-error">{{ errors.lastName }}</div>
      </div>
    </div>

    <div class="cm-field" :class="{ 'cm-invalid': !!errors.country }">
      <label class="cm-label" for="cm-country">{{ t('contactForm.country') }}</label>
      <select
        class="cm-select"
        id="cm-country"
        name="country"
        :value="model.country"
        @change="onInput('country', ($event.target as HTMLSelectElement).value)"
      >
        <option disabled value="">{{ t('contactForm.selectCountry') }}</option>
        <option v-for="o in countryOptions" :key="o.value" :value="o.value">
          {{ o.label }}
        </option>
      </select>
      <div class="cm-error">{{ errors.country }}</div>
    </div>

    <div class="cm-field" :class="{ 'cm-invalid': !!errors.phone }">
      <label class="cm-label" for="cm-phone">{{ t('contactForm.phone') }}</label>
      <input
        class="cm-input"
        id="cm-phone"
        name="phone"
        type="tel"
        autocomplete="tel"
        placeholder="+46 70 123 45 67"
        :value="model.phone"
        @input="onInput('phone', ($event.target as HTMLInputElement).value)"
      />
      <div class="cm-help">{{ t('contactForm.phoneHelp') }}</div>
      <div class="cm-error">{{ errors.phone }}</div>
    </div>

    <div class="cm-field">
      <label class="cm-check">
        <input
          id="cm-whatsapp"
          name="hasWhatsApp"
          type="checkbox"
          :checked="model.hasWhatsApp"
          @change="onInput('hasWhatsApp', ($event.target as HTMLInputElement).checked)"
        />
        <span>{{ t('contactForm.hasWhatsApp') }}</span>
      </label>
    </div>

    <div class="cm-field" :class="{ 'cm-invalid': !!errors.message }">
      <label class="cm-label" for="cm-message">{{ t('contactForm.message') }}</label>
      <textarea
        class="cm-textarea"
        id="cm-message"
        name="message"
        :placeholder="t('contactForm.messagePlaceholder')"
        :value="model.message"
        @input="onInput('message', ($event.target as HTMLTextAreaElement).value)"
      />
      <div class="cm-error">{{ errors.message }}</div>
    </div>
  </form>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'

  export type StepTwoModel = {
    firstName: string
    lastName: string
    country: string
    phone: string
    hasWhatsApp: boolean
    message: string
  }

  const props = defineProps<{
    model: StepTwoModel
    errors: Partial<Record<keyof StepTwoModel, string>>
    preferredCountries?: string[]
  }>()

  const emit = defineEmits<{
    (e: 'update:model', value: StepTwoModel): void
  }>()

  const { t } = useI18n({ useScope: 'global' })

  const onInput = <K extends keyof StepTwoModel>(key: K, value: StepTwoModel[K]) => {
    emit('update:model', { ...props.model, [key]: value })
  }

  function countryCodeToFlag(code: string): string {
    const c = code.toUpperCase()
    if (!/^[A-Z]{2}$/.test(c)) return ''
    const A = 0x1f1e6
    const base = 'A'.codePointAt(0) ?? 65
    const first = A + (c.codePointAt(0)! - base)
    const second = A + (c.codePointAt(1)! - base)
    return String.fromCodePoint(first, second)
  }

  function buildCountryOptions(preferred: string[]): Array<{ value: string; label: string }> {
    const locale = typeof navigator !== 'undefined' ? navigator.language : 'en-US'
    let regions: string[] = []

    const supportedValuesOf = (Intl as any).supportedValuesOf as
      | undefined
      | ((key: string) => string[])
    if (supportedValuesOf) {
      try {
        regions = supportedValuesOf('region')
      } catch {
        regions = []
      }
    }

    if (!regions.length) {
      regions = ['SE', 'NO', 'FI', 'DK', 'ES', 'DE', 'FR', 'GB', 'US', 'IT', 'NL', 'BE', 'PT', 'PL']
    }

    let displayNames: Intl.DisplayNames | null = null
    try {
      if ('DisplayNames' in Intl) {
        // @ts-expect-error runtime guard above
        displayNames = new Intl.DisplayNames([locale], { type: 'region' })
      }
    } catch {
      displayNames = null
    }

    const unique = Array.from(
      new Set(regions.map(r => r.toUpperCase()).filter(r => /^[A-Z]{2}$/.test(r)))
    )
    const opts = unique.map(iso2 => {
      const name = displayNames?.of(iso2) ?? iso2
      const flag = countryCodeToFlag(iso2)
      return { value: iso2, label: `${flag} ${name}`.trim() }
    })

    const preferredSet = new Set(preferred.map(p => p.toUpperCase()))
    opts.sort((a, b) => {
      const aPref = preferredSet.has(a.value)
      const bPref = preferredSet.has(b.value)
      if (aPref && !bPref) return -1
      if (!aPref && bPref) return 1
      if (aPref && bPref) return preferred.indexOf(a.value) - preferred.indexOf(b.value)
      return a.label.localeCompare(b.label)
    })

    return opts
  }

  const preferred = computed(
    () => props.preferredCountries ?? ['SE', 'NO', 'FI', 'DK', 'ES', 'DE', 'FR', 'GB', 'US']
  )
  const countryOptions = computed(() => buildCountryOptions(preferred.value))
</script>
